import { useMutation, useQueryClient } from '@tanstack/react-query';

import { ApiError } from '../../../shared/api-client/index.js';
import { useCurrentScope } from '../../../shared/scope/useCurrentScope.js';

import { createContact, getContactsQueryKey, type CreateContactInput, type ContactItem } from '../api/contacts.client.js';

export interface DuplicateContactSuggestion {
  id: string;
  fullName: string;
  email?: string | null;
}

export class DuplicateContactError extends Error {
  public readonly suggestion: DuplicateContactSuggestion;

  constructor(suggestion: DuplicateContactSuggestion) {
    super('duplicate contact');
    this.name = 'DuplicateContactError';
    this.suggestion = suggestion;
  }
}

export function useCreateContactMutation() {
  const queryClient = useQueryClient();
  const scope = useCurrentScope();
  const workspaceId = scope?.workspaceId ?? 'anonymous';

  return useMutation<ContactItem, Error, CreateContactInput>({
    mutationFn: async (input) => {
      try {
        return await createContact(input);
      } catch (error) {
        const status = error instanceof ApiError ? error.status : typeof error === 'object' && error !== null && 'status' in error ? error.status : undefined;
        const details = error instanceof ApiError ? error.details : typeof error === 'object' && error !== null && 'details' in error ? error.details : undefined;

        if (status === 409) {
          const suggestion = (details as { suggestion?: DuplicateContactSuggestion } | undefined)?.suggestion;
          if (suggestion) {
            throw new DuplicateContactError(suggestion);
          }
        }

        throw error instanceof Error ? error : new Error('failed to create contact');
      }
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: getContactsQueryKey(workspaceId) });
    },
  });
}
